"use client";

import { useRouter } from "next/navigation";
import RecommendedFeed from "@/components/feed/recommended-feed";
import FollowingFeed from "@/components/feed/following-feed";
import LiveFeed from "@/components/feed/live-feed";
import DiscoverFeed from "@/components/feed/discover-feed-v2";
import { FeedTopBar } from "@/components/feed/feed-top-bar";
import { SellHeroSection } from "@/components/feed/sell-hero-section";
import { ShopByCategory } from "@/components/feed/shop-by-category";
import { TrendingRail } from "@/components/feed/trending-rail";
import { ItemCardRail } from "@/components/feed/item-card-rail";
import { FollowSuggestionsCarousel } from "@/components/profile-page/follow-suggestions-carousel";
import { HandCoins } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FeedPageClientProps } from "@/components/feed/feed-page-client-types";
import { useFeedPageClientState } from "@/components/feed/feed-page-client-state";
import { useOverlay } from "@/components/context/overlay-context";
import {
  FEED_FOR_YOU_TAB_ENABLED,
  isFeedScrollableTab,
  isFeedSnapStyleTab,
} from "@/lib/feed-tab-config";

const OFFER_FORMATS_LIST = ["offers_only", "fixed_price_with_offers"];

export default function FeedPageClientMobile({ isLoggedIn, currentUserId, user }: FeedPageClientProps) {
  const router = useRouter();
  const {
    activeTab,
    discoverRefreshKey,
    filters,
    handleSearch,
    handleTabChange,
    isMobileSheetOpen,
    isTransitioning,
    setFilters,
    setIsMobileSheetOpen,
  } = useFeedPageClientState(isLoggedIn);
  // Spec §E 1.6a: discover-category slider itself lives one level up
  // (feed-page-client.tsx, above the desktop/mobile swap) on the shared
  // overlay panel slot — this client only needs to open it.
  const { openPanel } = useOverlay();

  const scrollableTab = isFeedScrollableTab(activeTab);

  // Spec §E 1.6b: search is a surface (route /search), not an overlay — the
  // desktop client (feed-page-client-desktop.tsx) already used the hook's
  // `handleSearch` (router.push) directly. This client used to shadow it with
  // a local re-definition that opened `FeedSearchTabSlider` instead (the
  // §B.4 reset-bug source: slider state died on the listing-detail route
  // unmount, so back landed on /feed with search gone). Now both breakpoints
  // share the identical mechanism — no local override.

  return (
    <div className="fixed inset-0 flex min-h-0 flex-col">
      <div className={isMobileSheetOpen ? "hidden" : ""}>
        <FeedTopBar
          activeTab={activeTab}
          onTabChangeAction={handleTabChange}
          isLoggedIn={isLoggedIn}
          onSearchAction={handleSearch}
          currentUserId={currentUserId}
          onFiltersChangeAction={setFilters}
          activeTabForFilters={activeTab}
          snapStyleNav={isFeedSnapStyleTab(activeTab)}
        />
      </div>

      <div
        className={cn(
          "feed-content-container min-h-0 flex-1",
          !isMobileSheetOpen &&
            "pb-[calc(3.5rem+var(--kraahl-safe-bottom))]",
          scrollableTab && "pt-(--feed-top-bar-height,4rem)",
          scrollableTab ? "overflow-y-auto" : "overflow-hidden"
        )}
        data-feed-scroll-root
      >
        <div
          className={cn(
            "feed-container mx-auto transition-opacity duration-150",
            scrollableTab ? "w-full max-w-(--breakpoint-lg) px-4 pt-3 pb-2" : "px-0! sm:px-0! w-full max-w-none h-full min-h-0 overflow-hidden",
            isTransitioning ? "opacity-0" : "opacity-100"
          )}
        >
          {FEED_FOR_YOU_TAB_ENABLED && activeTab === "for-you" ? (
            <RecommendedFeed
              filters={filters}
              currentUserId={currentUserId}
              isActive={activeTab === "for-you"}
              onMobileSheetChangeAction={setIsMobileSheetOpen}
            />
          ) : null}
          {activeTab === "following" && (
            <FollowingFeed
              isLoggedIn={isLoggedIn}
              currentUserId={currentUserId}
              onMobileSheetChangeAction={setIsMobileSheetOpen}
            />
          )}
          {activeTab === "live" ? (
            <>
              <SellHeroSection user={user ?? null} className="mb-6" />
              <ShopByCategory
                className="mb-10"
                onSelectAction={(id) => router.push(`/category/${id}`)}
              />
              <TrendingRail
                className="mb-7"
                currentUserId={currentUserId}
                onSeeAll={() => router.push("/trending")}
              />
              <ItemCardRail
                title="Open to offers"
                icon={HandCoins}
                currentUserId={currentUserId}
                listingFormats={OFFER_FORMATS_LIST}
                onSeeAll={() => router.push("/open-to-offers")}
                className="mb-7"
              />
              <FollowSuggestionsCarousel
                currentUserId={currentUserId ?? null}
                excludeProfileId={currentUserId}
                title="Sellers to discover"
                className="mb-8"
                foilFollow
              />
            </>
          ) : null}
          {activeTab === "live" && <LiveFeed refreshSignal={discoverRefreshKey} currentUserId={currentUserId} />}
          {activeTab === "discover" && (
            <DiscoverFeed
              refreshSignal={discoverRefreshKey}
              filters={filters}
              currentUserId={currentUserId}
              onViewAllCategoryAction={(selection) => openPanel({ kind: "discover-category", selection })}
            />
          )}
        </div>
      </div>
    </div>
  );
}
